/**
 * VersieHistoriePage — klant ziet history van website changes via AI executor.
 * Per versie: before / after content + rollback knop.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, Undo2, CheckCircle, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function VersieHistoriePage() {
  const { api } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api('get', '/api/ai/website-versions');
      setVersions(r.data.versions || []);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rollback = async (id) => {
    if (!window.confirm('Weet u zeker dat u deze wijziging wilt terugdraaien?')) return;
    setBusy(true);
    try {
      await api('post', `/api/ai/website-versions/${id}/rollback`);
      toast.success('Rollback aangevraagd');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };

  return (
    <div data-testid="versie-historie-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            <Clock size={18} style={{ display: 'inline', color: '#D4A017', marginRight: 8, verticalAlign: 'middle' }} />
            Versiehistorie — uitgevoerde AI wijzigingen
          </h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Alle door AI uitgevoerde website wijzigingen. U kunt elke wijziging terugdraaien.
          </p>
        </div>
        <button onClick={load} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <RefreshCw size={13} /> Vernieuwen
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden...</div>
      ) : versions.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #f1f5f9', padding: 40, textAlign: 'center' }}>
          <FileText size={28} style={{ color: '#D4A017', marginBottom: 12 }} />
          <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Nog geen versies</p>
          <p style={{ fontSize: 12, color: '#64748b' }}>Zodra AI wijzigingen voor u uitvoert, verschijnen die hier — inclusief 1-klik rollback.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {versions.map(v => (
            <div key={v.id} data-testid={`version-${v.id}`} style={{
              background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 16,
              opacity: v.rolled_back ? 0.6 : 1,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                <div>
                  <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{v.page_url}</p>
                  <p style={{ fontSize: 12, color: '#0f172a', fontWeight: 600, marginTop: 2 }}>{formatDate(v.created_at)}</p>
                </div>
                {v.rolled_back ? (
                  <span style={{ background: '#fef2f2', color: '#dc2626', padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>
                    Teruggedraaid · {formatDate(v.rolled_back_at)}
                  </span>
                ) : (
                  <button
                    data-testid={`rollback-${v.id}`}
                    onClick={() => rollback(v.id)}
                    disabled={busy}
                    style={{ background: 'white', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <Undo2 size={11} /> Rollback
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
                  <p style={{ fontSize: 9, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Voor</p>
                  <p style={{ fontSize: 11, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.4, marginTop: 2 }}>
                    "{v.before?.text || JSON.stringify(v.before).slice(0, 200)}"
                  </p>
                </div>
                <div style={{ background: '#f0fdf4', borderLeft: '3px solid #16a34a', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
                  <p style={{ fontSize: 9, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                    <CheckCircle size={9} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />Na (AI wijziging)
                  </p>
                  <p style={{ fontSize: 11, color: '#0f172a', fontWeight: 600, lineHeight: 1.4, marginTop: 2 }}>
                    {v.after?.text || JSON.stringify(v.after).slice(0, 200)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
